import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { QRCodeCanvas } from 'qrcode.react';
import { DishDialog } from '../components/DishDialog';
import LabelEditor from '../components/LabelEditor';
import { 
    getRestaurant, updateRestaurant, getCategories, createCategory, updateCategory, deleteCategory,
    getDishes, createDish, updateDish, deleteDish, uploadImage, uploadModel, uploadVideo, getFileUrl,
    getDishLabels, bulkUpdateDishLabels, downloadMenuPdf, importDishesCSV, reorderCategories, reorderDishes
} from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, Save, QrCode, Eye, Plus, Edit, Trash2, Upload, X, Download, 
    Palette, GripVertical, Image as ImageIcon, Box, Copy, Printer, ExternalLink, Tag, FileText, Video, Play, FileUp
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ id: itemId, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: itemId });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 50 : 'auto',
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className="flex items-center gap-2">
                <button {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-[#969282] hover:text-[#2B2A27] touch-none" data-testid={`drag-handle-${itemId}`}>
                    <GripVertical className="w-5 h-5" />
                </button>
                <div className="flex-1">{children}</div>
            </div>
        </div>
    );
}

export default function RestaurantDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const qrRef = useRef();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [restaurant, setRestaurant] = useState(null);
    const [categories, setCategories] = useState([]);
    const [dishes, setDishes] = useState([]);
    
    // Label editor state
    const [labelEditorDish, setLabelEditorDish] = useState(null);
    const [labelEditorLabels, setLabelEditorLabels] = useState([]);

    // Forms
    const [formData, setFormData] = useState({
        name: '', description: '', primary_color: '#C75D4E', background_color: '#F5F3E9', status: 'draft',
        enable_table_number: true, enable_car_number: false, notification_email: '',
        font_family: 'Playfair Display', card_style: 'glass', animation_style: 'fade',
        enable_whatsapp_share: true
    });
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '', sort_order: 0 });
    const [dishForm, setDishForm] = useState({ 
        name: '', description: '', price: '', category_id: '', image_url: '', model_url: '', video_url: '', display_type: 'auto',
        enable_image_view: true, enable_video_view: true, enable_ar_view: true
    });

    // Dialogs
    const [showCategoryDialog, setShowCategoryDialog] = useState(false);
    const [showDishDialog, setShowDishDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [editingDish, setEditingDish] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // File uploads
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingModel, setUploadingModel] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [importingCSV, setImportingCSV] = useState(false);

    const isNew = id === 'new';

    // DnD sensors
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    useEffect(() => {
        if (!isNew) {
            fetchData();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isNew]);

    const fetchData = async () => {
        try {
            const [restaurantRes, categoriesRes, dishesRes] = await Promise.all([
                getRestaurant(id),
                getCategories(id),
                getDishes(id)
            ]);
            setRestaurant(restaurantRes.data);
            setFormData({
                name: restaurantRes.data.name,
                description: restaurantRes.data.description || '',
                primary_color: restaurantRes.data.primary_color || '#C75D4E',
                background_color: restaurantRes.data.background_color || '#F5F3E9',
                status: restaurantRes.data.status || 'draft',
                logo_url: restaurantRes.data.logo_url || '',
                enable_table_number: restaurantRes.data.enable_table_number !== false,
                enable_car_number: restaurantRes.data.enable_car_number === true,
                notification_email: restaurantRes.data.notification_email || '',
                font_family: restaurantRes.data.font_family || 'Playfair Display',
                card_style: restaurantRes.data.card_style || 'glass',
                animation_style: restaurantRes.data.animation_style || 'fade',
                enable_whatsapp_share: restaurantRes.data.enable_whatsapp_share !== false
            });
            setCategories(categoriesRes.data);
            setDishes(dishesRes.data);
        } catch (err) {
            toast.error('Failed to load restaurant');
            navigate('/dashboard/restaurants');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Restaurant name is required');
            return;
        }
        setSaving(true);
        try {
            if (isNew) {
                const response = await (await import('../lib/api')).createRestaurant(formData);
                toast.success('Restaurant created');
                navigate(`/dashboard/restaurants/${response.data.id}`);
            } else {
                await updateRestaurant(id, formData);
                setRestaurant({ ...restaurant, ...formData });
                toast.success('Restaurant saved');
            }
        } catch (err) {
            toast.error('Failed to save restaurant');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingLogo(true);
        try {
            const response = await uploadImage(file);
            const url = getFileUrl(response.data.path);
            setFormData(prev => ({ ...prev, logo_url: url }));
            toast.success('Logo uploaded');
        } catch (err) {
            toast.error('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    // Category functions
    const openCategoryDialog = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setCategoryForm({ name: category.name, description: category.description || '', sort_order: category.sort_order || 0 });
        } else {
            setEditingCategory(null);
            setCategoryForm({ name: '', description: '', sort_order: categories.length });
        }
        setShowCategoryDialog(true);
    };

    const handleSaveCategory = async () => {
        if (!categoryForm.name.trim()) {
            toast.error('Category name is required');
            return;
        }
        try {
            if (editingCategory) {
                await updateCategory(editingCategory.id, categoryForm);
                toast.success('Category updated');
            } else {
                await createCategory(id, categoryForm);
                toast.success('Category created');
            }
            setShowCategoryDialog(false);
            fetchData();
        } catch (err) {
            toast.error('Failed to save category');
        }
    };

    // Dish functions
    const openDishDialog = (dish = null) => {
        if (dish) {
            setEditingDish(dish);
            setDishForm({
                name: dish.name,
                description: dish.description || '',
                price: String(dish.price),
                category_id: dish.category_id,
                image_url: dish.image_url || '',
                model_url: dish.model_url || '',
                video_url: dish.video_url || '',
                display_type: dish.display_type || 'auto',
                enable_image_view: dish.enable_image_view !== false,
                enable_video_view: dish.enable_video_view !== false,
                enable_ar_view: dish.enable_ar_view !== false
            });
        } else {
            setEditingDish(null);
            setDishForm({ name: '', description: '', price: '', category_id: categories[0]?.id || '', image_url: '', model_url: '', video_url: '', display_type: 'auto', enable_image_view: true, enable_video_view: true, enable_ar_view: true });
        }
        setShowDishDialog(true);
    };

    const handleSaveDish = async () => {
        if (!dishForm.name.trim() || !dishForm.price || !dishForm.category_id) {
            toast.error('Name, price, and category are required');
            return;
        }
        try {
            const data = { ...dishForm, price: parseFloat(dishForm.price) };
            if (editingDish) {
                await updateDish(editingDish.id, data);
                toast.success('Dish updated');
            } else {
                await createDish(id, data);
                toast.success('Dish created');
            }
            setShowDishDialog(false);
            fetchData();
        } catch (err) {
            toast.error('Failed to save dish');
        }
    };

    const handleDishImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingImage(true);
        try {
            const response = await uploadImage(file);
            const url = getFileUrl(response.data.path);
            setDishForm(prev => ({ ...prev, image_url: url }));
            toast.success('Image uploaded');
        } catch (err) {
            toast.error('Failed to upload image');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleModelUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingModel(true);
        try {
            const response = await uploadModel(file);
            const url = getFileUrl(response.data.path);
            setDishForm(prev => ({ ...prev, model_url: url }));
            toast.success('3D model uploaded');
        } catch (err) {
            toast.error('Failed to upload model');
        } finally {
            setUploadingModel(false);
        }
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingVideo(true);
        try {
            const response = await uploadVideo(file);
            const url = getFileUrl(response.data.path);
            setDishForm(prev => ({ ...prev, video_url: url }));
            toast.success('Video uploaded');
        } catch (err) {
            toast.error('Failed to upload video');
        } finally {
            setUploadingVideo(false);
        }
    };

    // Label editor functions
    const openLabelEditor = async (dish) => {
        try {
            const response = await getDishLabels(dish.id);
            setLabelEditorLabels(response.data);
            setLabelEditorDish(dish);
        } catch (err) {
            toast.error('Failed to load labels');
        }
    };

    const handleSaveLabels = async (labels) => {
        if (!labelEditorDish) return;
        await bulkUpdateDishLabels(labelEditorDish.id, labels);
        setLabelEditorDish(null);
        setLabelEditorLabels([]);
        fetchData();
    };

    const handleDownloadPdf = async () => {
        try {
            toast.info('Generating PDF...');
            const response = await downloadMenuPdf(id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${restaurant?.slug || 'menu'}-menu.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success('PDF downloaded!');
        } catch (err) {
            toast.error('Failed to generate PDF');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            if (deleteTarget.type === 'category') {
                await deleteCategory(deleteTarget.id);
                toast.success('Category deleted');
            } else {
                await deleteDish(deleteTarget.id);
                toast.success('Dish deleted');
            }
            fetchData();
        } catch (err) {
            toast.error('Failed to delete');
        } finally {
            setDeleteTarget(null);
        }
    };

    const downloadQR = () => {
        const canvas = qrRef.current?.querySelector('canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `${restaurant?.slug || 'menu'}-qr.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    // CSV Import
    const handleCSVImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportingCSV(true);
        try {
            const res = await importDishesCSV(id, file);
            const { created_dishes, created_categories, errors } = res.data;
            toast.success(`Imported ${created_dishes} dishes${created_categories > 0 ? `, created ${created_categories} categories` : ''}`);
            if (errors?.length > 0) {
                toast.warning(`${errors.length} rows skipped`);
            }
            fetchData();
        } catch (err) {
            toast.error('Failed to import CSV');
        } finally {
            setImportingCSV(false);
            e.target.value = '';
        }
    };

    // Drag & Drop handlers
    const handleCategoryDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = categories.findIndex(c => c.id === active.id);
        const newIndex = categories.findIndex(c => c.id === over.id);
        const newOrder = arrayMove(categories, oldIndex, newIndex);
        setCategories(newOrder);
        try {
            await reorderCategories(id, newOrder.map(c => c.id));
        } catch {
            toast.error('Failed to save order');
            fetchData();
        }
    };

    const handleDishDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = dishes.findIndex(d => d.id === active.id);
        const newIndex = dishes.findIndex(d => d.id === over.id);
        const newOrder = arrayMove(dishes, oldIndex, newIndex);
        setDishes(newOrder);
        try {
            await reorderDishes(id, newOrder.map(d => d.id));
        } catch {
            toast.error('Failed to save order');
            fetchData();
        }
    };

    const menuUrl = restaurant ? `${window.location.origin}/menu/${restaurant.slug}` : '';

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-4 border-[#C75D4E] border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate('/dashboard/restaurants')} data-testid="back-button">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-semibold text-[#2B2A27]">
                                {isNew ? 'New Restaurant' : formData.name}
                            </h1>
                            {!isNew && <p className="text-sm text-[#969282]">/menu/{restaurant?.slug}</p>}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {!isNew && restaurant?.status === 'published' && (
                            <Button variant="outline" onClick={() => window.open(menuUrl, '_blank')} className="gap-2" data-testid="view-menu-btn">
                                <Eye className="w-4 h-4" /> View Menu
                            </Button>
                        )}
                        {!isNew && (
                            <Button variant="outline" onClick={handleDownloadPdf} className="gap-2" data-testid="download-pdf-btn">
                                <FileText className="w-4 h-4" /> PDF
                            </Button>
                        )}
                        <Button onClick={handleSave} disabled={saving} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full gap-2" data-testid="save-restaurant-btn">
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>

                <Tabs defaultValue="details" className="space-y-6">
                    <TabsList className="bg-[#F0EDDF] p-1 rounded-xl">
                        <TabsTrigger value="details" className="rounded-lg data-[state=active]:bg-white">Details</TabsTrigger>
                        {!isNew && <TabsTrigger value="categories" className="rounded-lg data-[state=active]:bg-white">Categories</TabsTrigger>}
                        {!isNew && <TabsTrigger value="dishes" className="rounded-lg data-[state=active]:bg-white">Dishes</TabsTrigger>}
                        {!isNew && <TabsTrigger value="qrcode" className="rounded-lg data-[state=active]:bg-white">QR Code</TabsTrigger>}
                        {!isNew && <TabsTrigger value="preview" className="rounded-lg data-[state=active]:bg-white">Preview</TabsTrigger>}
                    </TabsList>

                    {/* Details Tab */}
                    <TabsContent value="details">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card className="border-[#E5E1D1]">
                                <CardHeader>
                                    <CardTitle className="text-lg">Basic Information</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <Label>Restaurant Name *</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Enter restaurant name"
                                            className="border-[#E5E1D1] rounded-xl"
                                            data-testid="restaurant-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Description</Label>
                                        <Textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Brief description of the restaurant"
                                            className="border-[#E5E1D1] rounded-xl"
                                            rows={3}
                                            data-testid="restaurant-description-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Logo</Label>
                                        <div className="flex items-center gap-4">
                                            {formData.logo_url && (
                                                <img src={formData.logo_url} alt="Logo" className="w-16 h-16 object-contain rounded-xl border border-[#E5E1D1]" />
                                            )}
                                            <label className="cursor-pointer">
                                                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                                                <Button type="button" variant="outline" className="gap-2" disabled={uploadingLogo} asChild>
                                                    <span>
                                                        <Upload className="w-4 h-4" />
                                                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                                                    </span>
                                                </Button>
                                            </label>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between pt-4 border-t border-[#E5E1D1]">
                                        <div>
                                            <Label className="text-base">Publish Menu</Label>
                                            <p className="text-sm text-[#969282]">Make the menu publicly accessible</p>
                                        </div>
                                        <Switch
                                            checked={formData.status === 'published'}
                                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, status: checked ? 'published' : 'draft' }))}
                                            data-testid="publish-toggle"
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border-[#E5E1D1]">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Palette className="w-5 h-5" /> Theme Builder
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Color Pickers */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Primary Color</Label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.primary_color}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                                                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]"
                                                    data-testid="primary-color-input"
                                                />
                                                <Input
                                                    value={formData.primary_color}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                                                    className="border-[#E5E1D1] rounded-xl flex-1"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Background Color</Label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="color"
                                                    value={formData.background_color}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                                                    className="w-12 h-12 rounded-lg cursor-pointer border-2 border-[#E5E1D1]"
                                                    data-testid="background-color-input"
                                                />
                                                <Input
                                                    value={formData.background_color}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                                                    className="border-[#E5E1D1] rounded-xl flex-1"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Theme Presets */}
                                    <div className="pt-4 border-t border-[#E5E1D1]">
                                        <Label className="mb-3 block">Quick Presets</Label>
                                        <div className="grid grid-cols-4 gap-2" data-testid="theme-presets">
                                            {[
                                                { key: 'classic_gold', name: 'Gold', p: '#C9A227', b: '#0D0D0D' },
                                                { key: 'modern_dark', name: 'Indigo', p: '#6366F1', b: '#111111' },
                                                { key: 'fresh_garden', name: 'Garden', p: '#22C55E', b: '#0A1A0F' },
                                                { key: 'royal_burgundy', name: 'Burgundy', p: '#DC2626', b: '#1A0A0A' },
                                                { key: 'ocean_breeze', name: 'Ocean', p: '#0EA5E9', b: '#0A1628' },
                                                { key: 'sunset_warm', name: 'Sunset', p: '#F97316', b: '#1A0F05' },
                                                { key: 'minimal_light', name: 'Light', p: '#2B2A27', b: '#F5F3E9' },
                                                { key: 'luxe_noir', name: 'Luxe', p: '#D4AF37', b: '#000000' },
                                            ].map(preset => (
                                                <button
                                                    key={preset.key}
                                                    type="button"
                                                    onClick={() => {
                                                        const presetData = {
                                                            classic_gold: { font_family: 'Playfair Display', card_style: 'glass', animation_style: 'fade' },
                                                            modern_dark: { font_family: 'Space Grotesk', card_style: 'solid', animation_style: 'slide' },
                                                            fresh_garden: { font_family: 'DM Sans', card_style: 'glass', animation_style: 'scale' },
                                                            royal_burgundy: { font_family: 'Cormorant Garamond', card_style: 'glass', animation_style: 'fade' },
                                                            ocean_breeze: { font_family: 'Outfit', card_style: 'solid', animation_style: 'slide' },
                                                            sunset_warm: { font_family: 'Poppins', card_style: 'glass', animation_style: 'scale' },
                                                            minimal_light: { font_family: 'Inter', card_style: 'solid', animation_style: 'fade' },
                                                            luxe_noir: { font_family: 'Bodoni Moda', card_style: 'glass', animation_style: 'fade' },
                                                        };
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            primary_color: preset.p,
                                                            background_color: preset.b,
                                                            ...(presetData[preset.key] || {})
                                                        }));
                                                    }}
                                                    className="flex flex-col items-center gap-1.5 p-2 rounded-xl border border-[#E5E1D1] hover:border-[#C9A227] transition-all hover:shadow-sm"
                                                    data-testid={`preset-${preset.key}`}
                                                >
                                                    <div className="w-full h-8 rounded-lg flex overflow-hidden">
                                                        <div className="flex-1" style={{ backgroundColor: preset.b }} />
                                                        <div className="w-3" style={{ backgroundColor: preset.p }} />
                                                    </div>
                                                    <span className="text-[10px] text-[#69665B]">{preset.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Font Family */}
                                    <div className="pt-4 border-t border-[#E5E1D1]">
                                        <Label className="mb-2 block">Menu Font</Label>
                                        <Select value={formData.font_family} onValueChange={(v) => setFormData(prev => ({ ...prev, font_family: v }))}>
                                            <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="font-family-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {['Playfair Display', 'Space Grotesk', 'DM Sans', 'Cormorant Garamond', 'Outfit', 'Poppins', 'Inter', 'Bodoni Moda'].map(f => (
                                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Card Style + Animation */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="mb-2 block">Card Style</Label>
                                            <Select value={formData.card_style} onValueChange={(v) => setFormData(prev => ({ ...prev, card_style: v }))}>
                                                <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="card-style-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="glass">Glass</SelectItem>
                                                    <SelectItem value="solid">Solid</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="mb-2 block">Animation</Label>
                                            <Select value={formData.animation_style} onValueChange={(v) => setFormData(prev => ({ ...prev, animation_style: v }))}>
                                                <SelectTrigger className="border-[#E5E1D1] rounded-xl" data-testid="animation-style-select">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="fade">Fade In</SelectItem>
                                                    <SelectItem value="slide">Slide Up</SelectItem>
                                                    <SelectItem value="scale">Scale Pop</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    
                                    {/* Preview */}
                                    <div className="pt-4 border-t border-[#E5E1D1]">
                                        <Label className="mb-3 block">Preview</Label>
                                        <div 
                                            className="rounded-xl p-6 text-center"
                                            style={{ backgroundColor: formData.background_color }}
                                        >
                                            <div 
                                                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3"
                                                style={{ backgroundColor: formData.primary_color }}
                                            >
                                                <span className="text-white font-bold">{formData.name?.charAt(0) || 'R'}</span>
                                            </div>
                                            <p className="font-semibold" style={{ color: formData.primary_color, fontFamily: formData.font_family }}>{formData.name || 'Restaurant Name'}</p>
                                            <div className="mt-3 mx-auto max-w-[160px] rounded-lg p-3 text-left" style={{
                                                backgroundColor: formData.card_style === 'glass' ? `${formData.primary_color}10` : `${formData.primary_color}08`,
                                                backdropFilter: formData.card_style === 'glass' ? 'blur(8px)' : 'none',
                                                border: `1px solid ${formData.primary_color}20`
                                            }}>
                                                <p className="text-xs" style={{ color: `${formData.primary_color}CC`, fontFamily: formData.font_family }}>Sample Dish</p>
                                                <p className="text-[10px]" style={{ color: `${formData.primary_color}60` }}>QAR 45</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Order Settings */}
                        <Card className="border-[#E5E1D1] mt-6">
                            <CardHeader>
                                <CardTitle className="text-lg">Order Settings</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${formData.enable_table_number ? 'border-[#C75D4E] bg-[#C75D4E]/5' : 'border-[#E5E1D1]'}`}>
                                    <div>
                                        <p className="font-medium text-[#2B2A27]">Table Number</p>
                                        <p className="text-xs text-[#69665B]">Customers can enter their table number at checkout</p>
                                    </div>
                                    <input type="checkbox" checked={formData.enable_table_number} onChange={e => setFormData(prev => ({ ...prev, enable_table_number: e.target.checked }))} className="accent-[#C75D4E] w-5 h-5" data-testid="toggle-table-number" />
                                </label>
                                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${formData.enable_car_number ? 'border-[#C75D4E] bg-[#C75D4E]/5' : 'border-[#E5E1D1]'}`}>
                                    <div>
                                        <p className="font-medium text-[#2B2A27]">Car Number</p>
                                        <p className="text-xs text-[#69665B]">Customers can enter their car/vehicle number at checkout</p>
                                    </div>
                                    <input type="checkbox" checked={formData.enable_car_number} onChange={e => setFormData(prev => ({ ...prev, enable_car_number: e.target.checked }))} className="accent-[#C75D4E] w-5 h-5" data-testid="toggle-car-number" />
                                </label>
                                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${formData.enable_whatsapp_share !== false ? 'border-[#25D366] bg-[#25D366]/5' : 'border-[#E5E1D1]'}`}>
                                    <div>
                                        <p className="font-medium text-[#2B2A27]">WhatsApp Sharing</p>
                                        <p className="text-xs text-[#69665B]">Show WhatsApp/SMS share buttons on checkout screen</p>
                                    </div>
                                    <input type="checkbox" checked={formData.enable_whatsapp_share !== false} onChange={e => setFormData(prev => ({ ...prev, enable_whatsapp_share: e.target.checked }))} className="accent-[#25D366] w-5 h-5" data-testid="toggle-whatsapp-share" />
                                </label>
                            </CardContent>
                        </Card>

                        {/* Notification Settings */}
                        <Card className="border-[#E5E1D1] mt-6">
                            <CardHeader>
                                <CardTitle className="text-lg">Email Notifications</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Notification Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.notification_email}
                                        onChange={e => setFormData(prev => ({ ...prev, notification_email: e.target.value }))}
                                        placeholder="kitchen@restaurant.com"
                                        className="border-[#E5E1D1] rounded-xl"
                                        data-testid="notification-email-input"
                                    />
                                    <p className="text-xs text-[#69665B]">Order notifications will be sent to this email and all assigned shop admins</p>
                                </div>
                                <div className="border-t border-[#E5E1D1] pt-4 space-y-3">
                                    <Label className="text-sm font-semibold">Live Orders Dashboard</Label>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            onClick={() => navigate(`/dashboard/live-orders/${id}`)}
                                            className="bg-[#C75D4E] hover:bg-[#A94E40] text-white gap-2 rounded-xl"
                                            data-testid="open-live-orders-btn"
                                        >
                                            Open Live Orders
                                        </Button>
                                        <Button
                                            variant="outline"
                                            className="gap-2 rounded-xl"
                                            data-testid="generate-kitchen-link-btn"
                                            onClick={async () => {
                                                try {
                                                    const { generateOrderKey } = await import('../lib/api');
                                                    const res = await generateOrderKey(id);
                                                    const key = res.data.order_access_key;
                                                    const link = `${window.location.origin}/live/${id}?key=${key}`;
                                                    await navigator.clipboard.writeText(link);
                                                    const { toast } = await import('sonner');
                                                    toast.success('Kitchen link copied to clipboard!');
                                                } catch (err) {
                                                    const { toast } = await import('sonner');
                                                    toast.error('Failed to generate link');
                                                }
                                            }}
                                        >
                                            Copy Kitchen Link
                                        </Button>
                                    </div>
                                    <p className="text-xs text-[#69665B]">Kitchen link allows staff to view live orders without logging in</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Categories Tab */}
                    <TabsContent value="categories">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Categories</CardTitle>
                                <Button onClick={() => openCategoryDialog()} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full gap-2" data-testid="add-category-btn">
                                    <Plus className="w-4 h-4" /> Add Category
                                </Button>
                            </CardHeader>
                            <CardContent>
                                {categories.length === 0 ? (
                                    <div className="text-center py-12 text-[#69665B]">
                                        No categories yet. Create your first category to organize dishes.
                                    </div>
                                ) : (
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                                        <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                            <div className="space-y-2">
                                                {categories.map((category) => (
                                                    <SortableItem key={category.id} id={category.id}>
                                                        <div className="flex items-center justify-between p-4 bg-[#F0EDDF]/50 rounded-xl" data-testid={`category-item-${category.id}`}>
                                                            <div>
                                                                <p className="font-medium text-[#2B2A27]">{category.name}</p>
                                                                {category.description && <p className="text-sm text-[#969282]">{category.description}</p>}
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm text-[#969282]">{dishes.filter(d => d.category_id === category.id).length} dishes</span>
                                                                <Button variant="ghost" size="icon" onClick={() => openCategoryDialog(category)} data-testid={`edit-category-${category.id}`}>
                                                                    <Edit className="w-4 h-4" />
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget({ type: 'category', id: category.id })} className="text-red-600" data-testid={`delete-category-${category.id}`}>
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </SortableItem>
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                )}
                                <p className="text-xs text-[#969282] mt-3">Drag to reorder categories</p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Dishes Tab */}
                    <TabsContent value="dishes">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>Dishes</CardTitle>
                                <div className="flex items-center gap-2">
                                    <label className="cursor-pointer">
                                        <input type="file" accept=".csv" onChange={handleCSVImport} className="hidden" data-testid="csv-import-input" />
                                        <Button type="button" variant="outline" className="rounded-full gap-2 border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10" disabled={importingCSV} asChild>
                                            <span>
                                                <FileUp className="w-4 h-4" />
                                                {importingCSV ? 'Importing...' : 'Import CSV'}
                                            </span>
                                        </Button>
                                    </label>
                                    <Button 
                                        onClick={() => openDishDialog()} 
                                        disabled={categories.length === 0}
                                        className="bg-[#C75D4E] hover:bg-[#A94E40] text-white rounded-full gap-2"
                                        data-testid="add-dish-btn"
                                    >
                                        <Plus className="w-4 h-4" /> Add Dish
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {/* CSV Format Help */}
                                <div className="mb-4 p-3 bg-[#F0EDDF]/50 rounded-xl text-xs text-[#69665B]">
                                    <p className="font-medium mb-1">CSV format: <code className="bg-white px-1.5 py-0.5 rounded text-[#C75D4E]">name, description, price, category</code></p>
                                    <p>Missing categories will be created automatically. Drag dishes to reorder.</p>
                                </div>
                                {categories.length === 0 ? (
                                    <div className="text-center py-12 text-[#69665B]">
                                        Create at least one category before adding dishes.
                                    </div>
                                ) : dishes.length === 0 ? (
                                    <div className="text-center py-12 text-[#69665B]">
                                        No dishes yet. Add your first dish or import from CSV.
                                    </div>
                                ) : (
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDishDragEnd}>
                                        <SortableContext items={dishes.map(d => d.id)} strategy={verticalListSortingStrategy}>
                                            <div className="space-y-2">
                                                {dishes.map((dish) => (
                                                    <SortableItem key={dish.id} id={dish.id}>
                                                        <div className="flex items-center justify-between p-3 bg-white border border-[#E5E1D1] rounded-xl" data-testid={`dish-item-${dish.id}`}>
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                <div className="w-12 h-12 rounded-lg bg-[#F0EDDF] flex-shrink-0 overflow-hidden">
                                                                    {dish.image_url ? (
                                                                        <img src={dish.image_url} alt={dish.name} className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center">
                                                                            <ImageIcon className="w-5 h-5 text-[#969282]" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="font-medium text-[#2B2A27] text-sm truncate">{dish.name}</p>
                                                                        {dish.video_url && <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Video</span>}
                                                                        {dish.model_url && <span className="text-[9px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full">3D</span>}
                                                                        {dish.label_count > 0 && <span className="text-[9px] bg-[#C9A227]/10 text-[#C9A227] px-1.5 py-0.5 rounded-full">{dish.label_count} labels</span>}
                                                                    </div>
                                                                    <p className="text-xs text-[#969282]">{categories.find(c => c.id === dish.category_id)?.name}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <p className="font-semibold text-[#C75D4E] text-sm">QAR {dish.price.toFixed(0)}</p>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDishDialog(dish)} data-testid={`edit-dish-${dish.id}`}>
                                                                    <Edit className="w-3.5 h-3.5" />
                                                                </Button>
                                                                {(dish.image_url || dish.video_url) && (
                                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#C9A227]" onClick={() => openLabelEditor(dish)} data-testid={`edit-labels-${dish.id}`}>
                                                                        <Tag className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                )}
                                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setDeleteTarget({ type: 'dish', id: dish.id })} data-testid={`delete-dish-${dish.id}`}>
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </SortableItem>
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* QR Code Tab */}
                    <TabsContent value="qrcode">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <QrCode className="w-5 h-5" /> QR Code & Share
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col items-center">
                                    {/* QR Code Preview */}
                                    <div 
                                        ref={qrRef} 
                                        className="p-8 rounded-2xl mb-6"
                                        style={{ backgroundColor: formData.background_color }}
                                    >
                                        <div className="text-center mb-4">
                                            {formData.logo_url && (
                                                <img 
                                                    src={formData.logo_url} 
                                                    alt={formData.name}
                                                    className="w-16 h-16 object-contain mx-auto rounded-xl mb-3"
                                                />
                                            )}
                                            <p 
                                                className="font-heading text-2xl font-light"
                                                style={{ color: formData.primary_color }}
                                            >
                                                {formData.name}
                                            </p>
                                            <p className="text-sm mt-1" style={{ color: `${formData.primary_color}80` }}>
                                                Scan to view our menu in AR
                                            </p>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl inline-block">
                                            <QRCodeCanvas 
                                                value={menuUrl} 
                                                size={180}
                                                level="H"
                                                fgColor={formData.primary_color}
                                            />
                                        </div>
                                    </div>

                                    {/* Menu URL */}
                                    <div className="w-full max-w-md mb-6">
                                        <Label className="text-sm text-[#69665B] mb-2 block">Public Menu URL</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                value={menuUrl}
                                                readOnly
                                                className="border-[#E5E1D1] rounded-xl bg-[#F0EDDF]/50 text-sm"
                                            />
                                            <Button 
                                                variant="outline" 
                                                onClick={() => {
                                                    navigator.clipboard.writeText(menuUrl);
                                                    toast.success('Link copied to clipboard!');
                                                }}
                                                className="gap-2"
                                                data-testid="copy-link-btn"
                                            >
                                                <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                onClick={() => window.open(menuUrl, '_blank')}
                                                className="gap-2"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="flex flex-wrap gap-3 justify-center mb-6">
                                        <Button 
                                            onClick={downloadQR} 
                                            variant="outline"
                                            className="rounded-full gap-2" 
                                            data-testid="download-qr-btn"
                                        >
                                            <Download className="w-4 h-4" /> Download QR
                                        </Button>
                                        <Button 
                                            onClick={() => navigate(`/dashboard/restaurants/${id}/qr-poster`)}
                                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white rounded-full gap-2"
                                            data-testid="open-poster-btn"
                                        >
                                            <Printer className="w-4 h-4" /> Open Print Poster
                                        </Button>
                                    </div>

                                    {/* Tips */}
                                    <div className="p-4 bg-[#F0EDDF]/50 rounded-xl max-w-md">
                                        <p className="text-sm font-medium text-[#2B2A27] mb-2">💡 Tips for QR codes</p>
                                        <ul className="text-xs text-[#69665B] space-y-1">
                                            <li>• Print at least 3x3 cm for easy scanning</li>
                                            <li>• Place at eye level on tables</li>
                                            <li>• Test the QR code before printing</li>
                                            <li>• Consider laminating for durability</li>
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Preview Tab */}
                    <TabsContent value="preview">
                        <Card className="border-[#E5E1D1]">
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <Eye className="w-5 h-5" /> Live Preview
                                </CardTitle>
                                <Button variant="outline" onClick={() => window.open(menuUrl, '_blank')} className="gap-2 rounded-full" data-testid="preview-open-new-tab">
                                    <ExternalLink className="w-4 h-4" /> Open in New Tab
                                </Button>
                            </CardHeader>
                            <CardContent>
                                <div className="rounded-2xl overflow-hidden border border-[#E5E1D1] bg-[#0D0D0D]" style={{ height: '680px' }}>
                                    <iframe
                                        src={menuUrl}
                                        className="w-full h-full"
                                        title="Menu Preview"
                                        style={{ maxWidth: '430px', margin: '0 auto', display: 'block', height: '100%' }}
                                        data-testid="menu-preview-iframe"
                                    />
                                </div>
                                <p className="text-xs text-[#69665B] text-center mt-3">This is a live preview of your public menu. Save changes first to see updates.</p>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Category Dialog */}
            <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
                        <DialogDescription>
                            {editingCategory ? 'Update the category details below.' : 'Add a new category to organize your dishes.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                            <Label>Category Name *</Label>
                            <Input
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Appetizers, Main Course"
                                className="border-[#E5E1D1] rounded-xl"
                                data-testid="category-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={categoryForm.description}
                                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Optional description"
                                className="border-[#E5E1D1] rounded-xl"
                                data-testid="category-description-input"
                            />
                        </div>
                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
                            <Button onClick={handleSaveCategory} className="bg-[#C75D4E] hover:bg-[#A94E40] text-white" data-testid="save-category-btn">
                                {editingCategory ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dish Dialog */}
            <DishDialog
                open={showDishDialog}
                onOpenChange={setShowDishDialog}
                editing={editingDish}
                dishForm={dishForm}
                setDishForm={setDishForm}
                categories={categories}
                onSave={handleSaveDish}
                onImageUpload={handleDishImageUpload}
                onModelUpload={handleModelUpload}
                onVideoUpload={handleVideoUpload}
                uploadingImage={uploadingImage}
                uploadingModel={uploadingModel}
                uploadingVideo={uploadingVideo}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {deleteTarget?.type === 'category' ? 'Category' : 'Dish'}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget?.type === 'category' 
                                ? 'This will delete the category and all its dishes. This action cannot be undone.'
                                : 'This will permanently delete this dish. This action cannot be undone.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-item-btn">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Label Editor */}
            {labelEditorDish && (
                <LabelEditor
                    imageUrl={labelEditorDish.image_url || labelEditorDish.video_url}
                    videoUrl={labelEditorDish.video_url}
                    labels={labelEditorLabels}
                    onSave={handleSaveLabels}
                    onClose={() => { setLabelEditorDish(null); setLabelEditorLabels([]); }}
                    primaryColor={formData.primary_color}
                />
            )}
        </DashboardLayout>
    );
}
